import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { authClient } from '../lib/auth-client'
import { clearAccessToken } from '../lib/api'

interface SessionUser {
  id: string
  email: string
  name?: string
  image?: string | null
  twoFactorEnabled?: boolean
}

interface SessionData {
  user: SessionUser
  session: { id: string }
}

export const useAdminAuth = defineStore('admin-auth', () => {
  const session = ref<SessionData | null>(null)
  const ready = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const isAuthenticated = computed(() => session.value !== null)

  async function init() {
    if (ready.value) return
    try {
      const { data } = await authClient.getSession()
      session.value = (data as SessionData | null) ?? null
    } catch {
      session.value = null
    } finally {
      ready.value = true
    }
  }

  async function requestOtp(email: string) {
    loading.value = true
    error.value = null
    try {
      const { error: e } = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: 'sign-in',
      })
      if (e) throw new Error(e.message ?? 'OTP send failed')
    } catch (e) {
      error.value = (e as Error).message
      throw e
    } finally {
      loading.value = false
    }
  }

  async function verifyOtp(email: string, otp: string) {
    loading.value = true
    error.value = null
    try {
      const { error: e } = await authClient.signIn.emailOtp({ email, otp })
      if (e) throw new Error(e.message ?? 'OTP invalide')
      const { data } = await authClient.getSession()
      session.value = (data as SessionData | null) ?? null
    } catch (e) {
      error.value = (e as Error).message
      throw e
    } finally {
      loading.value = false
    }
  }

  async function signInPasskey() {
    loading.value = true
    error.value = null
    try {
      const { error: e } = await authClient.signIn.passkey()
      if (e) throw new Error(e.message ?? 'Passkey failed')
      const { data } = await authClient.getSession()
      session.value = (data as SessionData | null) ?? null
    } catch (e) {
      error.value = (e as Error).message
      throw e
    } finally {
      loading.value = false
    }
  }

  async function registerPasskey(name?: string) {
    const opts = name ? { name } : {}
    const { error: e } = await authClient.passkey.addPasskey(opts)
    if (e) throw new Error(e.message ?? 'Passkey registration failed')
  }

  async function enableTotp(password?: string) {
    const { data, error: e } = await authClient.twoFactor.enable({ password: password ?? '' })
    if (e) throw new Error(e.message ?? 'TOTP enable failed')
    return data
  }

  async function verifyTotp(code: string) {
    const { error: e } = await authClient.twoFactor.verifyTotp({ code })
    if (e) throw new Error(e.message ?? 'TOTP invalide')
    const { data } = await authClient.getSession()
    session.value = (data as SessionData | null) ?? null
  }

  async function signOut() {
    await authClient.signOut()
    clearAccessToken()
    session.value = null
  }

  return {
    session,
    ready,
    loading,
    error,
    isAuthenticated,
    init,
    requestOtp,
    verifyOtp,
    signInPasskey,
    registerPasskey,
    enableTotp,
    verifyTotp,
    signOut,
  }
})
